import { Edit, PhotoCamera } from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';
import {
  Ref,
  useRef,
  useMemo,
  useState,
  useEffect,
  forwardRef,
  ReactElement,
} from 'react';
import {
  Box,
  Slide,
  Avatar,
  Dialog,
  IconButton,
  Typography,
  DialogContent,
} from '@mui/material';

import { UploadFile as UploadFileValue } from 'types';

import { FileDropbox } from '../file-dropbox';

import { UploadFileProps, UploadPhotoProps } from './types';

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: ReactElement<any, any>;
  },
  ref: Ref<unknown>
) {
  return <Slide ref={ref} direction="up" {...props} />;
});

export const UploadFile = ({
  src,
  open,
  error,
  label,
  accept,
  setFile,
  className,
  withoutCrop,
  onOpenChange,
  helperContent,
  renderTrigger,
  allowedFileSize,
  disabled = false,
  'aria-label': triggerLabel,
  ...props
}: UploadFileProps) => {
  const [errorValue, setError] = useState<string | null>(error ?? null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => setError(error ?? null), [error]);

  const disabledCloseRequested = useRef(false);
  useEffect(() => {
    if (!disabled || (!isOpen && !open)) {
      disabledCloseRequested.current = false;
      return;
    }
    if (!disabledCloseRequested.current) {
      disabledCloseRequested.current = true;
      setIsOpen(false);
      onOpenChange?.(false);
    }
  }, [disabled, isOpen, open, onOpenChange]);

  const handleClose = () => {
    if (open === undefined) {
      setIsOpen(false);
    }
    onOpenChange?.(false);
  };

  const handleOpen = () => {
    if (disabled) return;
    if (open === undefined) {
      setIsOpen(true);
    }
    onOpenChange?.(true);
  };

  const handleFileSet = (img: UploadFileValue | null) => {
    setFile(img);
    handleClose();
  };

  const isDialogOpen = open ?? isOpen;

  const renderTriggerElement = useMemo(
    () =>
      renderTrigger ? (
        renderTrigger(handleOpen)
      ) : (
        <IconButton
          disabled={disabled}
          onClick={handleOpen}
          className={className}
          aria-label={triggerLabel ?? 'Upload file'}
          sx={{
            p: 0,
            borderRadius: '50%',
            position: 'relative',
            '&:hover .edit-overlay': {
              opacity: 1,
            },
          }}
        >
          <Avatar src={src} {...props}>
            {!src && <PhotoCamera />}
          </Avatar>

          <Box
            className="edit-overlay"
            sx={{
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0,
              display: 'flex',
              borderRadius: '50%',
              position: 'absolute',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0, 0, 0, 0.4)',
              transition: 'opacity 0.2s ease',
            }}
          >
            <Edit sx={{ color: 'white' }} />
          </Box>
        </IconButton>
      ),
    [className, handleOpen, props, renderTrigger, src, disabled, triggerLabel]
  );

  return (
    <Box
      sx={{
        gap: 1,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
      }}
    >
      {renderTriggerElement}
      <Dialog
        onClose={handleClose}
        open={isDialogOpen && !disabled}
        slots={{ transition: Transition }}
        slotProps={{
          backdrop: {
            sx: {
              bgcolor: 'rgba(0, 0, 0, 0.7)',
            },
          },
          paper: {
            sx: {
              borderRadius: 2,
              bgcolor: 'background.paper',
            },
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <FileDropbox
            accept={accept}
            onClose={handleClose}
            setFile={handleFileSet}
            withoutCrop={withoutCrop}
            helperContent={helperContent}
            allowedFileSize={allowedFileSize}
          />
        </DialogContent>
      </Dialog>
      {errorValue ? (
        <Typography color="error" variant="body2">
          {errorValue}
        </Typography>
      ) : (
        label && <Typography variant="body2">{label}</Typography>
      )}
    </Box>
  );
};

export const UploadPhoto = UploadFile;

export type { UploadFileProps, UploadPhotoProps };
